import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { ApiError } from './api'
import { APP_BASE_PATH } from './constants'
import { isHandingOff } from './handoff'

// Track if we're already handling a 401 to prevent duplicate toasts/logouts
let isHandling401 = false

// 401 판별: status 기반(정확)을 우선하고, status 가 없는 레거시 Error 는 메시지로 폴백한다.
// (백엔드가 한국어 메시지로 401 을 주므로 메시지 매칭만으로는 누락된다.)
function isUnauthorized(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 401
  }
  return (
    error instanceof Error &&
    (error.message.includes('401') ||
      error.message === 'Unauthorized' ||
      error.message === 'Invalid token')
  )
}

function handle401Error(error: unknown) {
  const is401 = isUnauthorized(error)

  // 다른 배포로 넘어가는 중이면 그 이동을 취소하지 않는다 (lib/handoff.ts)
  if (!is401 || isHandling401 || isHandingOff()) return

  isHandling401 = true

  // Import dynamically to avoid circular dependency
  import('@/stores/authStore').then(({ useAuthStore }) => {
    const { isAuthenticated, logout } = useAuthStore.getState()

    if (isAuthenticated) {
      toast.error(i18n.t('errors.SESSION_EXPIRED'), {
        description: i18n.t('errors.SESSION_EXPIRED_DESC'),
      })
      logout().finally(() => {
        isHandling401 = false
        // 앱 베이스 경로로 이동한다. '/' 는 프로덕션(/image 하위 배포)에서 앱 밖으로 떨어진다.
        window.location.href = APP_BASE_PATH
      })
    } else {
      isHandling401 = false
    }
  })
}

/**
 * API 실패를 에러 리포트의 브레드크럼으로 남긴다 (리포트 자체는 아님 — 서버
 * 에러는 이미 서버 로그에 있다). errorReporter → authStore → queryClient 순환을
 * 피하기 위해 동적 import 를 쓴다 (handle401Error 의 authStore 패턴과 동일).
 */
function recordApiErrorBreadcrumb(error: unknown) {
  void import('@/lib/errorReporter').then(({ errorReporter }) => {
    const name = error instanceof Error ? error.name : 'Error'
    const message = error instanceof Error ? error.message : String(error)
    errorReporter.addBreadcrumb({
      type: 'api',
      message: `${name}: ${message}`,
      data: error instanceof ApiError ? { status: error.status } : undefined,
    })
  })
}

function handleGlobalError(error: unknown) {
  handle401Error(error)
  recordApiErrorBreadcrumb(error)
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 401 errors
        if (isUnauthorized(error)) {
          return false
        }
        return failureCount < 1
      },
    },
  },
  queryCache: new QueryCache({
    onError: handleGlobalError,
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalError,
  }),
})
